import { useEffect, useState, useMemo } from "react";
import {
  Button,
  Table,
  Spin,
  Alert,
  Space,
  message,
} from "antd";
import { ReloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { getPanchayatReportSachiv } from "../api/User";

/* =====================================================
   COMPONENT : GRAM SACHIV REPORT (PANCHAYAT WISE)
   NO DRILL DOWN - DIRECT PANCHAYAT LEVEL DATA ONLY
===================================================== */
const GramSachivReport = ({ dateFilter }) => {
  /* ================= STATE ================= */
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [userInfo, setUserInfo] = useState({
    district_code: null,
    district_name: null,
    block_code: null,
    block_name: null,
    panchayat_code: null,
    panchayat_name: null
  });

  // Get user info from session storage on component mount
  useEffect(() => {
    const districtName = sessionStorage.getItem("district_name");
    const districtCode = sessionStorage.getItem("district_code");
    const blockName = sessionStorage.getItem("block_name");
    const blockCode = sessionStorage.getItem("block_code");
    const panchayatName = sessionStorage.getItem("panchayat_name");
    const panchayatCode = sessionStorage.getItem("panchayat_code");

    setUserInfo({
      district_code: districtCode,
      district_name: districtName,
      block_code: blockCode,
      block_name: blockName,
      panchayat_code: panchayatCode,
      panchayat_name: panchayatName
    });
  }, []);

  const blockTotals = useMemo(() => {
    if (!data.length) return {
      total_gp: 0, gs_scheduled: 0, gs_conducted: 0,
      voter_updated: 0, attendance_updated: 0,
      image_uploaded: 0, proceeding_book_uploaded: 0,
      meeting_video_uploaded: 0, income_verified: 0,
      scheme_verified: 0, development_work_uploaded: 0,
      amrit_sarovar_uploaded: 0,
    };

    return data.reduce((acc, row) => ({
      total_gp: acc.total_gp + row.total_gp,
      gs_scheduled: acc.gs_scheduled + row.gs_scheduled,
      gs_conducted: acc.gs_conducted + row.gs_conducted,
      voter_updated: acc.voter_updated + row.voter_updated,
      attendance_updated: acc.attendance_updated + row.attendance_updated,
      image_uploaded: acc.image_uploaded + row.image_uploaded,
      proceeding_book_uploaded: acc.proceeding_book_uploaded + row.proceeding_book_uploaded,
      meeting_video_uploaded: acc.meeting_video_uploaded + row.meeting_video_uploaded,
      income_verified: acc.income_verified + row.income_verified,
      scheme_verified: acc.scheme_verified + row.scheme_verified,
      development_work_uploaded: acc.development_work_uploaded + row.development_work_uploaded,
      amrit_sarovar_uploaded: acc.amrit_sarovar_uploaded + row.amrit_sarovar_uploaded,
    }), {
      total_gp: 0, gs_scheduled: 0, gs_conducted: 0,
      voter_updated: 0, attendance_updated: 0,
      image_uploaded: 0, proceeding_book_uploaded: 0,
      meeting_video_uploaded: 0, income_verified: 0,
      scheme_verified: 0, development_work_uploaded: 0,
      amrit_sarovar_uploaded: 0,
    });
  }, [data]);

  // ================= SORT HELPERS =================
  const textSorter = (a, b, key) => {
    const valA = (a[key] || "").toString().toLowerCase();
    const valB = (b[key] || "").toString().toLowerCase();
    return valA.localeCompare(valB);
  };

  const numberSorter = (a, b, key) => {
    const numA = parseFloat(a[key]) || 0;
    const numB = parseFloat(b[key]) || 0;
    return numA - numB;
  };

  /* ================= MAIN TABLE COLUMNS - PANCHAYAT WISE WITH DISTRICT & BLOCK ================= */
  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "sr_no",
      width: 70,
      align: "center",
      fixed: "left",
      render: (text, record, index) => index + 1,
      sorter: (a, b, c) => {
        const aIndex = data.findIndex(item => item.block_code === a.block_code);
        const bIndex = data.findIndex(item => item.block_code === b.block_code);
        return aIndex - bIndex;
      },
    },
    {
      title: "District Name",
      dataIndex: "district_name",
      width: 180,
      align: "left",
      sorter: (a, b) => textSorter(a, b, "district_name"),
      sortDirections: ["ascend", "descend"],
      fixed: "left",

    },
    {
      title: "Block Name",
      dataIndex: "block_name",
      width: 200,
      align: "left",
      sorter: (a, b) => textSorter(a, b, "block_name"),
      sortDirections: ["ascend", "descend"],
      fixed: "left",

    },
    {
      title: "Panchayat Name",
      dataIndex: "panchayat_name",
      width: 220,
      align: "left",
      sorter: (a, b) => textSorter(a, b, "panchayat_name"),
      sortDirections: ["ascend", "descend"],

    },
    {
      title: "Total Gram Panchayat where Gram Sabha Scheduled",
      dataIndex: "gs_scheduled",
      width: 320,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "gs_scheduled"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Total Gram Panchayat where Gram Sabha Conducted",
      dataIndex: "gs_conducted",
      width: 320,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "gs_conducted"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "No. of Panchayat where Voter Number Updated",
      dataIndex: "voter_updated",
      width: 320,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "voter_updated"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "No. of Panchayat where Attendance Updated",
      dataIndex: "attendance_updated",
      width: 320,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "attendance_updated"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Number of Panchayat where Image Uploaded",
      dataIndex: "image_uploaded",
      width: 320,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "image_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Number of Panchayat where Proceeding Book Uploaded",
      dataIndex: "proceeding_book_uploaded",
      width: 360,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "proceeding_book_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Number of Panchayat where Meeting Video Uploaded",
      dataIndex: "meeting_video_uploaded",
      width: 360,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "meeting_video_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    ...(dateFilter !== "after" ? [
      {
        title: "Income Verified",
        dataIndex: "income_verified",
        width: 180,
        align: "center",
        sorter: (a, b) => numberSorter(a, b, "income_verified"),
        sortDirections: ["ascend", "descend"],
      },
      {
        title: "Scheme Verified",
        dataIndex: "scheme_verified",
        width: 180,
        align: "center",
        sorter: (a, b) => numberSorter(a, b, "scheme_verified"),
        sortDirections: ["ascend", "descend"],
      }
    ] : []),
    {
      title: "Number of Panchayat where Development Work Uploaded",
      dataIndex: "development_work_uploaded",
      width: 380,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "development_work_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Total Panchayat where Detail of Amrit Sarovar Uploaded",
      dataIndex: "amrit_sarovar_uploaded",
      width: 380,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "amrit_sarovar_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
  ];

  /* ================= FETCH PANCHAYAT DATA ================= */
  useEffect(() => {
    fetchPanchayatData();
  }, []);

  // Re-fetch when dateFilter changes
  useEffect(() => {
    if (dateFilter !== undefined) {
      fetchPanchayatData();
    }
  }, [dateFilter]);

  const fetchPanchayatData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem("token");

      if (!token) {
        setError("No authentication token found. Please login again.");
        setLoading(false);
        return;
      }

      const result = await getPanchayatReportSachiv(token, { period: dateFilter });

      console.log("API Response:", result);

      if (result.status === 'success' && result.data) {
        const formattedData = result.data.map((item, index) => ({
          key: item.panchayat_code || `row-${index}`,
          panchayat_code: item.panchayat_code,
          panchayat_name: item.panchayat_name || "N/A",
          block_code: item.block_code,
          block_name: item.block_name || "N/A",
          district_code: item.district_code,
          district_name: item.district_name || "N/A",
          total_gp: Number(item.total_gp) || 1,
          gs_scheduled: Number(item.gs_scheduled) || 0,
          gs_conducted: Number(item.gs_conducted) || 0,
          voter_updated: Number(item.voter_updated) || 0,
          attendance_updated: Number(item.attendance_updated) || 0,
          image_uploaded: Number(item.image_uploaded) || 0,
          proceeding_book_uploaded: Number(item.proceeding_book_uploaded) || 0,
          meeting_video_uploaded: Number(item.meeting_video_uploaded) || 0,
          income_verified: Number(item.income_verified) || 0,
          scheme_verified: Number(item.scheme_verified) || 0,
          development_work_uploaded: Number(item.development_work_uploaded) || 0,
          amrit_sarovar_uploaded: Number(item.amrit_sarovar_uploaded) || 0,
        }));

        setData(formattedData);
        message.success(`Loaded ${formattedData.length} panchayat record(s)`);
      } else {
        setError(result.message || 'Failed to fetch panchayat data');
        message.error(result.message || 'Failed to fetch panchayat data');
      }
    } catch (err) {
      console.error('Error fetching panchayat data:', err);
      const errorMessage = err.message || 'Failed to fetch panchayat data. Please try again.';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /* ================= EXPORT FUNCTION ================= */
  const handleExportExcel = () => {
    if (!data || data.length === 0) {
      message.warning("No data to export");
      return;
    }

    try {
      const excelData = data.map((row, idx) => ({
        "Sr. No.": idx + 1,
        "District Name": row.district_name || "N/A",
        "Block Name": row.block_name || "N/A",
        "Panchayat Name": row.panchayat_name || "N/A",
        "Total GP": row.total_gp || 0,
        "GS Scheduled": row.gs_scheduled || 0,
        "GS Conducted": row.gs_conducted || 0,
        "Voter Updated": row.voter_updated || 0,
        "Attendance Updated": row.attendance_updated || 0,
        "Image Uploaded": row.image_uploaded || 0,
        "Proceeding Book Uploaded": row.proceeding_book_uploaded || 0,
        "Meeting Video Uploaded": row.meeting_video_uploaded || 0,
        ...(dateFilter !== "after" && {
          "Income Verified": row.income_verified || 0,
          "Scheme Verified": row.scheme_verified || 0,
        }),
        "Development Work Uploaded": row.development_work_uploaded || 0,
        "Amrit Sarovar Uploaded": row.amrit_sarovar_uploaded || 0,
      }));

      excelData.push({
        "Sr. No.": "",
        "District Name": "",
        "Block Name": "",
        "Panchayat Name": "GRAND TOTAL",
        "Total GP": blockTotals.total_gp,
        "GS Scheduled": blockTotals.gs_scheduled,
        "GS Conducted": blockTotals.gs_conducted,
        "Voter Updated": blockTotals.voter_updated,
        "Attendance Updated": blockTotals.attendance_updated,
        "Image Uploaded": blockTotals.image_uploaded,
        "Proceeding Book Uploaded": blockTotals.proceeding_book_uploaded,
        "Meeting Video Uploaded": blockTotals.meeting_video_uploaded,
        ...(dateFilter !== "after" && {
          "Income Verified": blockTotals.income_verified,
          "Scheme Verified": blockTotals.scheme_verified,
        }),
        "Development Work Uploaded": blockTotals.development_work_uploaded,
        "Amrit Sarovar Uploaded": blockTotals.amrit_sarovar_uploaded,
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Panchayat Report");

      // Add district and block to filename for better identification
      const districtPart = userInfo.district_name?.replace(/\s+/g, '_') || 'District';
      const blockPart = userInfo.block_name?.replace(/\s+/g, '_') || 'Block';
      const fileName = `Gram_Sachiv_Report_${districtPart}_${blockPart}_${new Date().toISOString().split('T')[0]}.xlsx`;

      XLSX.writeFile(workbook, fileName);
      message.success("Excel file exported successfully");
    } catch (err) {
      console.error("Export Excel error:", err);
      message.error("Failed to export Excel file");
    }
  };


  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };
  /* ================= UI ================= */
  return (
    <div className="report-container">
      <div className="report-card">
        <div className="report-header">
          <div>
            <h2 className="report-title">Detailed report of Special Gram Sabha (Gram Sachiv)</h2>
            <div style={{ color: '#666', marginTop: 8, fontSize: 14, display: 'flex', flexWrap: 'wrap', gap: '16px' }}>

            </div>
          </div>

          <Space>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              disabled={loading || data.length === 0}
            >
              Export Excel
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchPanchayatData}>
              Refresh
            </Button>
          </Space>
        </div>

        {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <p style={{ marginTop: 10, color: '#666' }}>Loading panchayat data...</p>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            rowKey={(record) => record.panchayat_code || record.key}
            bordered
            size="small"
            scroll={{ x: 3800, y: 420 }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: data.length,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} records`,
            }}
            onChange={handleTableChange}
          />
        )}
        <div style={totalBoxStyle}>
          <span><strong>Totals:</strong></span>
          <span>Total GP: <strong>{blockTotals.total_gp.toLocaleString()}</strong></span>
          <span>GS Scheduled: <strong>{blockTotals.gs_scheduled.toLocaleString()}</strong></span>
          <span>GS Conducted: <strong>{blockTotals.gs_conducted.toLocaleString()}</strong></span>
          <span>Voter Updated: <strong>{blockTotals.voter_updated.toLocaleString()}</strong></span>
          <span>Attendance Updated: <strong>{blockTotals.attendance_updated.toLocaleString()}</strong></span>
          <span>Images Uploaded: <strong>{blockTotals.image_uploaded.toLocaleString()}</strong></span>
          <span>Proceeding Book: <strong>{blockTotals.proceeding_book_uploaded.toLocaleString()}</strong></span>
          <span>Meeting Video: <strong>{blockTotals.meeting_video_uploaded.toLocaleString()}</strong></span>
          {dateFilter !== "after" && (
            <>
              <span>Income Verified: <strong>{blockTotals.income_verified.toLocaleString()}</strong></span>
              <span>Scheme Verified: <strong>{blockTotals.scheme_verified.toLocaleString()}</strong></span>
            </>
          )}
          <span>Dev Work: <strong>{blockTotals.development_work_uploaded.toLocaleString()}</strong></span>
          <span>Amrit Sarovar: <strong>{blockTotals.amrit_sarovar_uploaded.toLocaleString()}</strong></span>
        </div>
      </div>


    </div>
  );
};

/* ================= STYLES ================= */
const totalBoxStyle = {
  backgroundColor: "#e6f7ff",
  border: "1px solid #91d5ff",
  borderRadius: "4px",
  padding: "8px 12px",
  marginTop: "8px",
  display: "flex",
  flexWrap: "wrap",
  gap: "12px",
  fontSize: "11px",
};

const positiveStyle = {
  color: "#52c41a",
  fontWeight: 500,
};

const negativeStyle = {
  color: "#ff4d4f",
  fontWeight: 400,
};

export default GramSachivReport;